import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { Modal } from '../../../components/Modal';
import { dmsService } from '../../../services/dmsService';
import { userService } from '../../../services/userService';
import { SUCCESS_MESSAGES } from '../../../utils/successMessages';

interface EditDocModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  onMetadataCreated?: () => void;
  categories: any[];
  documentTypes: any[];
  document: any | null;
}

export const EditDocModal: React.FC<EditDocModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onMetadataCreated,
  categories,
  documentTypes,
  document,
}) => {
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const canCreateDocType = currentUser?.role === 'super_admin' || currentUser?.role === 'admin';
  const canCreateCategory = currentUser?.role === 'super_admin' || currentUser?.role === 'admin';
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [departments, setDepartments] = useState<any[]>([]);
  const [newCategoryDeptId, setNewCategoryDeptId] = useState('');

  useEffect(() => {
    if (isOpen && isSuperAdmin) {
      userService.getDepartments().then(setDepartments).catch((err) => {
        console.error('Failed to fetch departments', err);
      });
    }
  }, [isOpen, isSuperAdmin]);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    document_type: '',
    confidentiality: 'INTERNAL',
    is_archived: false,
  });
  const [isNewDocType, setIsNewDocType] = useState(false);
  const [newDocTypeName, setNewDocTypeName] = useState('');
  const [newDocTypeDesc, setNewDocTypeDesc] = useState('');

  const [isNewCategory, setIsNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDesc, setNewCategoryDesc] = useState('');

  useEffect(() => {
    if (isOpen && document) {
      setFormData({
        title: document.title || '',
        description: document.description || '',
        category: document.category_detail?.id || '',
        document_type: document.document_type_detail?.id || '',
        confidentiality: document.confidentiality || 'INTERNAL',
        is_archived: document.is_archived || false,
      });
      setIsNewDocType(false);
      setNewDocTypeName('');
      setNewDocTypeDesc('');
      setIsNewCategory(false);
      setNewCategoryName('');
      setNewCategoryDesc('');
      setNewCategoryDeptId('');
      setError(null);
      setSuccessMessage(null);
    }
  }, [isOpen, document]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!document) return;
    if (!formData.category && !isNewCategory) {
      setError('Please select a category.');
      return;
    }
    if (!formData.document_type && !isNewDocType) {
      setError('Please select a document type.');
      return;
    }
    if (isNewCategory && isSuperAdmin && !newCategoryDeptId) {
      setError('Please select a department for the new category.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      let finalCategoryId = formData.category;
      if (isNewCategory) {
        const newCat = await dmsService.createCategory({
          name: newCategoryName,
          description: newCategoryDesc,
          ...(isSuperAdmin ? { department: newCategoryDeptId } : {}),
        });
        finalCategoryId = newCat.id;
      }

      let finalDocTypeId = formData.document_type;
      if (isNewDocType) {
        const newType = await dmsService.createDocumentType({
          name: newDocTypeName,
          description: newDocTypeDesc,
        });
        finalDocTypeId = newType.id;
      }

      const updatePayload = {
        title: formData.title,
        description: formData.description,
        category: finalCategoryId,
        document_type: finalDocTypeId,
        confidentiality: formData.confidentiality,
        is_archived: formData.is_archived,
      };

      await dmsService.updateDocument(document.id, updatePayload);
      onSave(); // Refresh main list + close modal
    } catch (err: any) {
      console.error('Failed to update document metadata:', err);
      setError(err.message || 'Failed to update document metadata.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveNewCategory = async () => {
    if (!newCategoryName.trim()) {
      setError('Please enter a name for the new category.');
      return;
    }
    if (isSuperAdmin && !newCategoryDeptId) {
      setError('Please select a department for the new category.');
      return;
    }
    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const createdCategory = await dmsService.createCategory({
        name: newCategoryName.trim(),
        description: newCategoryDesc.trim(),
        ...(isSuperAdmin ? { department: newCategoryDeptId } : {}),
      });
      setFormData((prev) => ({ ...prev, category: createdCategory.id }));
      setIsNewCategory(false);
      setNewCategoryName('');
      setNewCategoryDesc('');
      setNewCategoryDeptId('');
      setSuccessMessage(SUCCESS_MESSAGES.CATEGORY_CREATED);
      // Refresh categories/departments in the parent without closing this modal.
      onMetadataCreated?.();
    } catch (err: any) {
      console.error('Failed to create Category:', err);
      setError(err.message || 'Failed to create Category.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveNewDocType = async () => {
    if (!newDocTypeName.trim()) {
      setError('Please enter a name for the new document type.');
      return;
    }
    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const createdType = await dmsService.createDocumentType({
        name: newDocTypeName.trim(),
        description: newDocTypeDesc.trim(),
      });
      setFormData((prev) => ({ ...prev, document_type: createdType.id }));
      setIsNewDocType(false);
      setNewDocTypeName('');
      setNewDocTypeDesc('');
      setSuccessMessage(SUCCESS_MESSAGES.DOCUMENT_TYPE_CREATED);
      // Refresh document types in the parent without closing this modal.
      onMetadataCreated?.();
    } catch (err: any) {
      console.error('Failed to create Document Type:', err);
      setError(err.message || 'Failed to create Document Type.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Document Metadata">
      <form onSubmit={handleSubmit}>
        <div className="modal-body">
          {error && (
            <div
              style={{
                color: '#dc2626',
                backgroundColor: '#fee2e2',
                padding: '0.75rem',
                borderRadius: '8px',
                marginBottom: '1rem',
                fontSize: '0.9rem',
                fontWeight: 600,
              }}
            >
              {error}
            </div>
          )}

          {successMessage && (
            <div
              style={{
                color: '#15803d',
                backgroundColor: '#dcfce7',
                padding: '0.75rem',
                borderRadius: '8px',
                marginBottom: '1rem',
                fontSize: '0.9rem',
                fontWeight: 600,
              }}
            >
              {successMessage}
            </div>
          )}

          <div className="form-row">
            <div className="filter-group">
              <label>Document Title *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
              />
            </div>
            <div className="filter-group">
              <label>Category *</label>
              {!isNewCategory ? (
                <select
                  name="category"
                  value={formData.category}
                  onChange={(e) => {
                    if (e.target.value === 'NEW') {
                      setIsNewCategory(true);
                    } else {
                      setFormData({ ...formData, category: e.target.value });
                    }
                  }}
                  required
                >
                  <option value="">Select Category</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.department_detail?.name ? `${c.name} (${c.department_detail.name})` : c.name}
                    </option>
                  ))}
                  {canCreateCategory && (
                    <option value="NEW">+ Create New Category</option>
                  )}
                </select>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.65rem',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    padding: '0.85rem',
                    backgroundColor: '#f9fafb',
                  }}
                >
                  <input
                    type="text"
                    placeholder="New Category Name (e.g. Legal Agreements)"
                    required
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                  />
                  {isSuperAdmin ? (
                    <select
                      value={newCategoryDeptId}
                      onChange={(e) => setNewCategoryDeptId(e.target.value)}
                      required
                    >
                      <option value="">Select Department</option>
                      {departments.map((d) => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={currentUser?.department || ''}
                      disabled
                      className="field-disabled"
                    />
                  )}
                  <input
                    type="text"
                    placeholder="Description (optional)"
                    value={newCategoryDesc}
                    onChange={(e) => setNewCategoryDesc(e.target.value)}
                  />
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem' }}>
                    <button
                      type="button"
                      className="primary-btn"
                      style={{ fontSize: '0.85rem', padding: '0.4rem 0.85rem' }}
                      onClick={handleSaveNewCategory}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Saving...' : 'Save Category'}
                    </button>
                    <button
                      type="button"
                      className="secondary-btn"
                      style={{ fontSize: '0.85rem', padding: '0.4rem 0.85rem' }}
                      onClick={() => setIsNewCategory(false)}
                      disabled={isLoading}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="filter-group form-full">
              <label>Confidentiality Rating</label>
              <select
                name="confidentiality"
                value={formData.confidentiality}
                onChange={handleChange}
              >
                <option value="PUBLIC">Public</option>
                <option value="INTERNAL">Internal</option>
                <option value="CONFIDENTIAL">Confidential</option>
                <option value="RESTRICTED">Restricted</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="filter-group form-full">
              <label>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                style={{
                  padding: '0.6rem 0.8rem',
                  borderRadius: '8px',
                  border: '1px solid var(--border)',
                  fontFamily: 'inherit',
                  fontSize: '0.95rem',
                  backgroundColor: '#f9fafb',
                  resize: 'vertical',
                }}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="filter-group form-full">
              <label>Document Type *</label>
              {!isNewDocType ? (
                <select
                  name="document_type"
                  value={formData.document_type}
                  onChange={(e) => {
                    if (e.target.value === 'NEW') {
                      setIsNewDocType(true);
                    } else {
                      setFormData({ ...formData, document_type: e.target.value });
                    }
                  }}
                  required
                >
                  <option value="">Select Document Type</option>
                  {documentTypes.map((dt) => (
                    <option key={dt.id} value={dt.id}>
                      {dt.name}
                    </option>
                  ))}
                  {canCreateDocType && (
                    <option value="NEW">+ Create New Type</option>
                  )}
                </select>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.65rem',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    padding: '0.85rem',
                    backgroundColor: '#f9fafb',
                  }}
                >
                  <input
                    type="text"
                    placeholder="New Document Type Name (e.g. Agreement)"
                    required
                    value={newDocTypeName}
                    onChange={(e) => setNewDocTypeName(e.target.value)}
                  />
                  <input
                    type="text"
                    placeholder="Description (optional)"
                    value={newDocTypeDesc}
                    onChange={(e) => setNewDocTypeDesc(e.target.value)}
                  />
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem' }}>
                    <button
                      type="button"
                      className="primary-btn"
                      style={{ fontSize: '0.85rem', padding: '0.4rem 0.85rem' }}
                      onClick={handleSaveNewDocType}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Saving...' : 'Save Document Type'}
                    </button>
                    <button
                      type="button"
                      className="secondary-btn"
                      style={{ fontSize: '0.85rem', padding: '0.4rem 0.85rem' }}
                      onClick={() => setIsNewDocType(false)}
                      disabled={isLoading}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="secondary-btn"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button type="submit" className="primary-btn" disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </Modal>
  );
};
